import type { LegState, MatchConfig, Player, TeamPlayMode } from "../types.js";

export function supportsTeamPlay(gameType: MatchConfig["gameType"]): boolean {
  return gameType === "x01" || gameType === "cricket";
}

export function normalizeTeamPlay(raw: MatchConfig): TeamPlayMode {
  if (!supportsTeamPlay(raw.gameType)) return "solo";
  if (raw.teamPlay === "league" || raw.teamPlay === "team") return raw.teamPlay;
  return "solo";
}

export function isTeamFormat(config: Pick<MatchConfig, "teamPlay" | "gameType">): boolean {
  return normalizeTeamPlay(config as MatchConfig) !== "solo";
}

export const PLAYERS_PER_TEAM = 2;
export const TEAM_COUNT_OPTIONS = [2, 3, 4] as const;
const TEAM_FALLBACK = ["Team 1", "Team 2", "Team 3", "Team 4"];

export function fallbackTeamLabel(side: number): string {
  return TEAM_FALLBACK[side] ?? `Team ${side + 1}`;
}

export function clampTeamCount(n: number): number {
  if (!Number.isFinite(n)) return 2;
  return Math.min(4, Math.max(2, Math.round(n)));
}

export function playingTeamCount(config: Pick<MatchConfig, "players" | "teamPlay" | "gameType">): number {
  const n = config.players?.length ?? 0;
  if (!isTeamFormat(config)) return Math.max(1, n);
  return clampTeamCount(n / PLAYERS_PER_TEAM);
}

export function defaultTeamFirstThrower(teamCount: number): number {
  return clampTeamCount(teamCount) > 2 ? 0 : PLAYERS_PER_TEAM;
}

export function teamSlotCount(teamCount: number): number {
  return clampTeamCount(teamCount) * PLAYERS_PER_TEAM;
}

/** Team 0 = indices 0,1; team 1 = 2,3; … */
export function sideOf(playerIndex: number): number {
  return Math.floor(Math.max(0, playerIndex) / PLAYERS_PER_TEAM);
}

export function partnerIndex(playerIndex: number): number {
  return playerIndex ^ 1;
}

export function sideIndices(side: number): [number, number] {
  const base = Math.max(0, side) * PLAYERS_PER_TEAM;
  return [base, base + 1];
}

export function teammateIndices(playerIndex: number): [number, number] {
  return sideIndices(sideOf(playerIndex));
}

export function opponentIndices(playerIndex: number, playerCount = 4): number[] {
  const teams = clampTeamCount(playerCount / PLAYERS_PER_TEAM);
  const side = sideOf(playerIndex);
  const out: number[] = [];
  for (let t = 0; t < teams; t++) {
    if (t === side) continue;
    out.push(...sideIndices(t).filter((i) => i < playerCount));
  }
  return out;
}

export function opposingSideLeaders(playerIndex: number, playerCount = 4): number[] {
  return opponentIndices(playerIndex, playerCount).filter((i) => i % PLAYERS_PER_TEAM === 0);
}

/**
 * Player-index groups per team: [[A1, A2], [B1, B2], …].
 * Layout is team-major. Throw order interleaves by slot.
 */
export function teamGroups(config: Pick<MatchConfig, "players" | "teamPlay" | "gameType">): number[][] {
  const n = Math.max(1, config.players?.length ?? 1);
  if (!isTeamFormat(config) || n < 4) {
    return Array.from({ length: n }, (_, i) => [i]);
  }
  const count = playingTeamCount(config);
  return Array.from({ length: count }, (_, t) => sideIndices(t).filter((i) => i < n));
}

/**
 * Team A starts: A1 – B1 – C1 – A2 – B2 – C2 – …
 * Team B starts: B1 – C1 – A1 – B2 – C2 – A2 – …
 */
export function throwOrder(config: MatchConfig, firstThrowerIndex = config.firstThrowerIndex): number[] {
  const n = Math.max(1, config.players?.length ?? 1);
  const groups = teamGroups(config);
  if (!isTeamFormat(config) || groups.length < 2) {
    const seq = config.throwSequence;
    if (seq?.length) {
      const seen = new Set<number>();
      const out: number[] = [];
      for (const raw of seq) {
        const i = Math.round(Number(raw));
        if (!Number.isInteger(i) || i < 0 || i >= n || seen.has(i)) continue;
        seen.add(i);
        out.push(i);
      }
      for (let i = 0; i < n; i++) {
        if (!seen.has(i)) out.push(i);
      }
      return out;
    }
    return Array.from({ length: n }, (_, i) => i);
  }
  const start = Math.min(Math.max(0, firstThrowerIndex ?? 0), n - 1);
  let startTeam = groups.findIndex((g) => g.includes(start));
  if (startTeam < 0) startTeam = 0;
  const slots = Math.max(...groups.map((g) => g.length));
  const order: number[] = [];
  for (let slot = 0; slot < slots; slot++) {
    for (let t = 0; t < groups.length; t++) {
      const idx = groups[(startTeam + t) % groups.length]![slot];
      if (idx !== undefined) order.push(idx);
    }
  }
  return order;
}

export function nextThrowerIndex(
  config: MatchConfig,
  from: number,
  eliminated: boolean[],
  firstThrowerIndex = config.firstThrowerIndex,
): number {
  const order = throwOrder(config, firstThrowerIndex);
  const pos = order.indexOf(from);
  const start = pos >= 0 ? pos : 0;
  for (let step = 1; step <= order.length; step++) {
    const i = order[(start + step) % order.length]!;
    if (!eliminated[i]) return i;
  }
  return from;
}

/** Next leg: the following team throws first, still with its first player. */
export function nextTeamFirstThrower(config: MatchConfig, prevFirst: number): number {
  const groups = teamGroups(config);
  const n = Math.max(1, config.players?.length ?? 1);
  if (groups.length < 2) return (prevFirst + 1) % n;
  let t = groups.findIndex((g) => g.includes(prevFirst));
  if (t < 0) t = 0;
  return groups[(t + 1) % groups.length]![0] ?? 0;
}

export function opposingTeamRemainingSums(leg: LegState, playerIndex: number): number[] {
  const n = leg.players.length;
  return opposingSideLeaders(playerIndex, n).map((lead) =>
    sideIndices(sideOf(lead)).reduce((sum, i) => sum + (leg.players[i]?.remaining ?? 0), 0),
  );
}

/**
 * Team-Modus 501: Ausmachen gesperrt, wenn der Partner mehr Rest hat als jedes
 * gegnerische Team zusammen (bei zwei Teams: mehr als beide Gegner).
 */
export function isCheckoutFrozen(leg: LegState, playerIndex: number): boolean {
  const partnerRemaining = leg.players[partnerIndex(playerIndex)]?.remaining ?? 0;
  const sums = opposingTeamRemainingSums(leg, playerIndex);
  return sums.length > 0 && sums.every((sum) => partnerRemaining > sum);
}

export function teamClosedNumber(leg: LegState, playerIndex: number, key: string): boolean {
  return teammateIndices(playerIndex).every((i) => (leg.players[i]?.marks[key] ?? 0) >= 3);
}

export function opposingTeamOpenNumber(leg: LegState, playerIndex: number, key: string): boolean {
  return opponentIndices(playerIndex, leg.players.length).some((i) => (leg.players[i]?.marks[key] ?? 0) < 3);
}

export function teamCricketScore(leg: LegState, playerIndex: number): number {
  return teammateIndices(playerIndex).reduce((sum, i) => sum + (leg.players[i]?.cricketScore ?? 0), 0);
}

export function sideLabel(config: MatchConfig, side: number): string {
  const idxs = sideIndices(side);
  const named = config.players[idxs[0]]?.teamName?.trim();
  if (named) return named;
  const fromTeams = config.teams?.[side]?.name?.trim();
  if (fromTeams) return fromTeams;
  return fallbackTeamLabel(side);
}

export function sidePlayerNames(config: MatchConfig, side: number): string {
  return sideIndices(side)
    .map((i) => config.players[i]?.name?.trim())
    .filter((n): n is string => Boolean(n))
    .join(", ");
}

export function winnerLabel(config: MatchConfig, winnerIndex: number, players?: Player[]): string {
  const roster = players ?? config.players;
  if (!isTeamFormat(config)) return roster[winnerIndex]?.name ?? "";
  return sideLabel(config, sideOf(winnerIndex));
}

export function creditIndices(config: MatchConfig, winnerIndex: number): number[] {
  if (!isTeamFormat(config) || (config.players?.length ?? 0) < 4) return [winnerIndex];
  return [...teammateIndices(winnerIndex)];
}

export function uniqueSideCount(values: number[], config: MatchConfig, threshold: number): number {
  if (!isTeamFormat(config) || values.length < 4) {
    return values.filter((n) => n >= threshold).length;
  }
  return teamGroups(config).filter((group) => (values[group[0] ?? 0] ?? 0) >= threshold).length;
}
